// lib/licensing/guard.ts
//
// Verifica la licencia on-prem en el cliente. Reusa los primitives puros
// de convex/system/licensing/crypto.ts (que es runtime-agnóstico: Web Crypto
// funciona igual en isolate, Node y Tauri webview).
//
// Devuelve un resultado discriminated union fácil de consumir por el gate
// de React: o pasa (allowed) o se bloquea con un motivo legible.

import {
  importPublicKey,
  verifyLicenseToken,
  evaluateLicense,
  type LicensePayload,
  type VerifyResult,
} from "@/convex/system/licensing/crypto";
import {
  LICENSE_PUBLIC_KEY,
  PUBLIC_KEY_NOT_CONFIGURED,
} from "./public-key";
import {
  loadLastHeartbeatAt,
  loadLicenseToken,
} from "./storage";
import { getEdition } from "../edition/edition";

export type GuardResult =
  | { allowed: true; payload: LicensePayload }
  | { allowed: false; reason: GuardReason };

export type GuardReason =
  | "public_key_not_configured"
  | "no_token"
  | "verify_failed"
  | "wrong_edition"
  | "expired"
  | "grace_exceeded"
  | "no_heartbeat_yet";

const REASON_LABEL: Record<GuardReason, string> = {
  public_key_not_configured:
    "La aplicación no está correctamente configurada. Contacta al administrador.",
  no_token: "Pega el token de licencia que recibiste al comprar Heskala.",
  verify_failed:
    "El token de licencia no es válido. Verifica que lo copiaste completo.",
  wrong_edition:
    "Este token es para otra edición de Heskala. Pide a tu proveedor una licencia para la edición on-prem.",
  expired: "La licencia expiró. Contacta a tu proveedor para renovarla.",
  grace_exceeded:
    "No se pudo validar la licencia en línea durante el período de gracia. Reconecta a internet para renovar la validación.",
  no_heartbeat_yet:
    "La licencia requiere una primera validación en línea. Verifica tu conexión a internet.",
};

// Nombre legible de cada edición para los mensajes del gate.
const EDITION_NAME: Record<string, string> = {
  onprem: "On-prem",
  free_desktop: "Heskala Free",
  desktop: "Desktop",
  saas: "SaaS",
};

export function guardReasonLabel(reason: GuardReason): string {
  // El mensaje de edición equivocada nombra la edición REAL de este build
  // (antes decía "on-prem" fijo, confundiendo en builds Free/Desktop).
  if (reason === "wrong_edition") {
    const name = EDITION_NAME[getEdition()] ?? "esta edición";
    return `Este token es para otra edición de Heskala. Pedí una licencia para ${name}.`;
  }
  return REASON_LABEL[reason];
}

let cachedKey: CryptoKey | null = null;

async function getPublicKey(): Promise<CryptoKey | null> {
  if (cachedKey) return cachedKey;
  if (!LICENSE_PUBLIC_KEY) return null;
  cachedKey = await importPublicKey(LICENSE_PUBLIC_KEY);
  return cachedKey;
}

/**
 * Evalúa la licencia almacenada localmente. NO llama al endpoint de heartbeat
 * (eso lo hace el componente de UI después, si hay internet).
 */
export async function evaluateOnPremLicense(): Promise<GuardResult> {
  if (!LICENSE_PUBLIC_KEY) {
    return { allowed: false, reason: "public_key_not_configured" };
  }

  const token = loadLicenseToken();
  if (!token) {
    return { allowed: false, reason: "no_token" };
  }

  const publicKey = await getPublicKey();
  if (!publicKey) {
    return { allowed: false, reason: "public_key_not_configured" };
  }

  const verify: VerifyResult = await verifyLicenseToken(token, publicKey);
  if (!verify.valid) {
    return { allowed: false, reason: "verify_failed" };
  }

  // La edición esperada es la del build: un build free_desktop solo acepta
  // tokens `ed: "free_desktop"`, un build onprem solo `ed: "onprem"`.
  const edition = getEdition() as LicensePayload["ed"];

  const lastOk = loadLastHeartbeatAt();
  // free_desktop no exige heartbeat (ni la primera validación online): se le
  // permite arrancar sin haber validado nunca. El resto de ediciones sí.
  if (edition !== "free_desktop" && lastOk === null) {
    return { allowed: false, reason: "no_heartbeat_yet" };
  }

  const evaluation = evaluateLicense(verify.payload, edition, lastOk);
  if (!evaluation.allowed) {
    if (evaluation.state === "wrong_edition") {
      return { allowed: false, reason: "wrong_edition" };
    }
    if (evaluation.state === "expired") {
      return { allowed: false, reason: "expired" };
    }
    return { allowed: false, reason: "grace_exceeded" };
  }

  return { allowed: true, payload: verify.payload };
}

export { PUBLIC_KEY_NOT_CONFIGURED };
